<template>
  <div class="garage-sync" />
</template>

<script>

/**
 * Vue: Garage sync (garage-sync)
 * -----------------------------------------------------------------------------
 * Global component to synchronise vehicle garage with customer metafield and
 * handles display of garage notifications.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import { fetchApiData } from '~/helpers/garage'

export default {
  name: 'GarageSync',

  data() {
    return {
      cnvsGlobal: true,
      controller: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeVehicle: 'garage/getActiveVehicle',
      garageCount: 'garage/getGarageCount',
      getFormattedVehicle: 'garage/getFormattedVehicle',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      garageState: (state) => state.garage,
    }),
  },

  watch: {

    /**
     * Watch active vehicle to display notifications.
     * @param {import("~stores/garage").Vehicle} newVehicle - New vehicle.
     * @param {import("~stores/garage").Vehicle} oldVehicle - Old vehicle.
     */
    activeVehicle(newVehicle, oldVehicle) {
      let event = 'activated'
      let vehicle = newVehicle

      /**
       * If no old then active vehicle has been added.
       */
      if (!oldVehicle) {
        event = 'added'
      }

      /**
       * If no new vehicle then active vehicle has been deactivated.
       */
      if (!newVehicle) {
        event = 'deactivated'
        vehicle = oldVehicle
      }

      /**
       * Vehicle is swapped when both old and new vehicles exist.
       * - If old and new IDs match then update triggered by MOT refresh and
       *   update should be ignored.
       */
      if (newVehicle && oldVehicle) {
        if (newVehicle?.id === oldVehicle?.id) {
          return
        }

        event = 'swapped'
      }

      this.displayNotification({ event, oldVehicle, vehicle })
    },

    /**
     * Watch garage count to check when a vehicle is removed.
     * @param {Number} newCount - New garage count.
     * @param {Number} oldCount - Old garage count.
     */
    async garageCount(newCount, oldCount) {
      if (newCount >= oldCount) {
        return
      }

      /**
       * Retrieve removed vehicle from session storage.
       * - Can't compare old and new garage as it requires a deep watcher, and
       *   deep watchers return the same old and new value.
       */
      await this.$nextTick()
      let vehicle = sessionStorage.getItem('cnvs-removed-vehicle')

      if (!vehicle) {
        return
      }

      vehicle = JSON.parse(vehicle)

      this.displayNotification({ event: 'removed', vehicle })
    },

    /**
     * Watch show compatibility to update metafield.
     */
    'garageState.showCompatibility'() {
      this.updateCustomerMetafield()
    },
  },

  mounted() {
    if (
      !this.$variable('customer.loggedIn') ||
      !this.$variable('credentials.customApis.customerUpdate')
    ) {
      return
    }

    this.syncGarage()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setActiveVehicle: 'garage/setActive',
      setNotification: 'setNotification',
      updateGarage: 'garage/updateGarage',
    }),

    /**
     * Synchronise customer metafield with local garage.
     */
    syncGarage() {
      const metafield = this.$variable('customer.garage')

      /**
       * If no metafield then update from scratch.
       */
      if (!metafield || !metafield.garage) {
        this.updateCustomerMetafield()
        return
      }

      /**
       * If garages match then do nothing.
       * - Metafield.garage is already stringified.
       */
      if (JSON.stringify(metafield.garage) === JSON.stringify(this.garageState)) {
        return
      }

      /**
       * If garages don't match then merge with priority given to local garage.
       */
      const metafieldObject = metafield.garage

      /**
       * Create combined garage, removing duplicate.
       */
      const mergedGarage = [
        ...this.garageState.garage,
        ...metafieldObject.garage,
      ]

      const garage = []

      mergedGarage.forEach((vehicle) => {
        const alreadyExists = garage.find((garageVehicle) => {
          return garageVehicle.id === vehicle.id
        })

        if (alreadyExists) {
          return
        }

        garage.push(vehicle)
      })

      /**
       * Use local active if it exists, otherwise use metafield active.
       */
      const active = this.garageState.active
        ? this.garageState.active
        : metafieldObject.active

      const combinedObject = {
        active,
        garage,
        showCompatibility: this.garageState.showCompatibility,
      }

      /**
       * Update local garage and metafield.
       */
      this.updateGarage(garage)
      this.setActiveVehicle(active)
      this.updateCustomerMetafield(combinedObject)
    },

    /**
     * Update customer metafield.
     * @param {Object} value - Value to update metafield with, leave blank to
     * generate automatically.
     * @returns {Promise}
     */
    updateCustomerMetafield(value) {
      return new Promise(async(resolve, reject) => {
        try {
          if (
            !this.$variable('customer.loggedIn') ||
            !this.$variable('customer.id') ||
            !this.$variable('credentials.customApis.customerUpdate')
          ) {
            resolve()
            return
          }

          if (this.controller.abort) {
            this.controller.abort()
          }

          this.controller = new AbortController()

          /**
           * Build values data.
           */
          const localValue = value ? value : {}

          if (!value) {
            Object.keys(this.garageState).forEach((key) => {
              localValue[key] = this.garageState[key]
            })
          }

          /**
           * Get fetch data.
           */
          const response = await fetchApiData({
            bodyData: {
              id: this.$variable('customer.id'),
              key: 'garage',
              value: JSON.stringify(localValue),
            },
            signal: this.controller.signal,
            url: this.$variable('credentials.customApis.customerUpdate'),
          })

          resolve(response)

        } catch (error) {
          cnvs.ReportError('Failed to update customer metafield', error)
          reject(error)
        }
      })
    },

    /**
     * Display garage notification.
     * - Wait until customer metafield has been updated if user is logged in.
     * - Close active notification.
     * @param {String} event - Type of event.
     * @param {import("~stores/garage").Vehicle} [oldVehicle] - Old vehicle.
     * @param {import("~stores/garage").Vehicle} vehicle - New vehicle.
     */
    async displayNotification({ event, oldVehicle, vehicle }) {
      await this.updateCustomerMetafield()

      /**
       * Determine notification message.
       */
      const formattedNewVehicle = this.getFormattedVehicle(vehicle)
      const formattedOldVehicle = this.getFormattedVehicle(oldVehicle)

      const string = `garage.notifications.${event}`

      const title = this.$string(string, {
        replace: {
          new_vehicle: formattedNewVehicle?.strings?.title,
          old_vehicle: formattedOldVehicle?.strings?.title,
        },
      })

      this.setNotification({
        timeout: this.$timing('message'),
        title,
        type: 'vehicle',
      })
    },
  },
}
</script>
